'use client'

import { useCallback } from 'react'

import { Button, Toast } from '@graftcode/design-system'

import type { ToastAction, ToastItem } from '@common/core/stores'
import { useToastStore } from '@common/core/stores'
import { resolveToastViewDuration } from '@common/core/stores/toast/utils/resolveToastViewDuration'

import styles from './ToastInstance.module.scss'

type Props = {
  toast: ToastItem
}

export function ToastInstance({ toast }: Props) {
  const removeToast = useToastStore((state) => state.removeToast)

  const handleClose = useCallback(() => removeToast(toast.id), [removeToast, toast.id])

  const handleActionClick = useCallback(
    (action: ToastAction) => {
      action.onClick?.()
      removeToast(toast.id)
    },
    [removeToast, toast.id]
  )

  const hasActions = Boolean(toast.primaryAction || toast.secondaryAction)

  const toastContent = (
    <Toast
      title={toast.title}
      description={toast.description}
      variant={toast.variant}
      duration={resolveToastViewDuration(toast.duration)}
      position={toast.position}
      onClose={handleClose}
    >
      {hasActions ? (
        <div className={styles.actions}>
          {toast.primaryAction && (
            <Button
              size="s"
              variant={toast.primaryAction.variant ?? 'primary'}
              data-testid={toast.primaryAction.testId}
              onClick={() => handleActionClick(toast.primaryAction!)}
            >
              {toast.primaryAction.label}
            </Button>
          )}
          {toast.secondaryAction && (
            <Button
              size="s"
              variant={toast.secondaryAction.variant ?? 'ghost'}
              data-testid={toast.secondaryAction.testId}
              onClick={() => handleActionClick(toast.secondaryAction!)}
            >
              {toast.secondaryAction.label}
            </Button>
          )}
        </div>
      ) : null}
    </Toast>
  )

  if (!toast.testId) {
    return toastContent
  }

  return <div data-testid={toast.testId}>{toastContent}</div>
}
